import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Search, BookOpen, GraduationCap } from "lucide-react";
import { PublicShell } from "@/components/site/PublicShell";
import { GLOSSARY } from "@/lib/glossary";
import { DEEP } from "@/lib/deepGlossary";
import { GROUPS, groupOf, lessonFor } from "@/lib/glossaryLessons";

// Every ⓘ definition in the app, grouped and searchable. Same source as the tooltips, so the two can never drift apart.
const hashKey = (hash) => decodeURIComponent((hash || "").replace(/^#term-/, "")) || null;

const Entry = ({ k, title, text, group, focused }) => {
  const d = DEEP[k];
  const lesson = lessonFor(k, group);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (focused && d) setOpen(true); }, [focused, d]);
  return (
    <div className={`rounded-lg border p-3 scroll-mt-28 transition-colors duration-700 ${focused ? "border-cyan-400 bg-cyan-500/10 ring-2 ring-cyan-400/60 shadow-[0_0_24px_rgba(34,211,238,0.25)]" : "border-slate-800 bg-slate-950/40"}`}
         data-testid={`glossary-term-${k}`} data-focused={focused ? "true" : undefined} id={`term-${k}`}>
      <div className="font-display text-sm font-semibold text-slate-100">{title}</div>
      <div className="text-xs text-slate-300 mt-1 leading-snug">{text}</div>
      {d && (
        <div className="mt-1.5">
          <button onClick={() => setOpen((v) => !v)} className="text-[11px] text-cyan-300 hover:underline" data-testid={`glossary-term-more-${k}`}>{open ? "Less" : "More detail"}</button>
          {open && (
            <div className="mt-1.5 space-y-1 text-[11px] text-slate-300">
              {d.why && <div><span className="text-cyan-300 font-semibold">Why it matters: </span>{d.why}</div>}
              {d.how && <div><span className="text-cyan-300 font-semibold">How it works: </span>{d.how}</div>}
              {d.action && <div><span className="text-cyan-300 font-semibold">What to do with it: </span>{d.action}</div>}
              {d.example && <div className="text-slate-400 italic">Example: {d.example}</div>}
            </div>
          )}
        </div>
      )}
      {lesson && (
        <Link to={`/learn/${lesson.slug}`} className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-cyan-300" data-testid={`glossary-learn-${k}`} title="Opens the Learn Crypto lesson (sign-in required)">
          <GraduationCap className="w-3.5 h-3.5" /> Learn more: <span className="text-slate-300">{lesson.title}</span>
        </Link>
      )}
    </div>
  );
};

export default function Glossary() {
  const { hash } = useLocation();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const setQ = (v) => setParams(v ? { q: v } : {}, { replace: true });
  const [focused, setFocused] = useState(() => hashKey(hash));
  const entries = useMemo(() => Object.entries(GLOSSARY).map(([k, [title, text]]) => ({ k, title, text, group: groupOf(k) })), []);
  const needle = q.trim().toLowerCase();
  const shown = needle ? entries.filter((e) => `${e.title} ${e.text} ${DEEP[e.k]?.how || ""}`.toLowerCase().includes(needle)) : entries;
  const groups = GROUPS.map(([g]) => [g, shown.filter((e) => e.group === g)]).filter(([, l]) => l.length);
  const target = focused && GLOSSARY[focused] ? focused : null;

  // Deep link from a ⓘ dialog: clear any search, scroll the definition into view and highlight it for a few seconds.
  useEffect(() => {
    const k = hashKey(hash);
    if (!k || !GLOSSARY[k]) return;
    setParams({}, { replace: true }); setFocused(k);
    const raf = requestAnimationFrame(() => document.getElementById(`term-${k}`)?.scrollIntoView({ behavior: "smooth", block: "center" }));
    const t = setTimeout(() => setFocused(null), 6000);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [hash, setParams]);

  return (
    <PublicShell wide title="Terms explained" subtitle="Every ⓘ explanation in EV-IDEX, in plain language and in one place. Definitions describe exactly how the app computes each number — nothing here is financial advice.">
      <div className="space-y-6" data-testid="glossary-page">
        <div className="flex flex-wrap items-center gap-3">
          <label className="relative flex-1 min-w-[240px] max-w-xl">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setFocused(null); }} placeholder="Search a term, e.g. R:R, strength, sealed record…" data-testid="glossary-search"
                   className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-cyan-400" />
          </label>
          <div className="text-xs text-slate-500 font-mono-ticker" data-testid="glossary-count"><BookOpen className="w-3.5 h-3.5 inline mr-1" />{shown.length} of {entries.length} terms</div>
        </div>
        {target && <div className="text-xs text-cyan-200/90" data-testid="glossary-jump-note">Showing the definition of <span className="font-semibold">{GLOSSARY[target][0]}</span> — highlighted below.</div>}
        {!groups.length && <div className="text-sm text-slate-400" data-testid="glossary-empty">No term matches "{q}". Try a shorter word.</div>}
        {groups.map(([g, list]) => (
          <section key={g} data-testid={`glossary-group-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <h2 className="font-display font-semibold text-base md:text-lg text-slate-100 mb-2">{g} <span className="text-xs font-normal text-slate-500">· {list.length}</span></h2>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{list.map((e) => <Entry key={e.k} {...e} focused={e.k === target} />)}</div>
          </section>
        ))}
      </div>
    </PublicShell>
  );
}
